/**
 * Unified form validation utilities
 * Consolidates validation logic used across different modals and forms
 */

// Email validation
export const validateEmail = (email: string): boolean => {
  if (!email.trim()) return true; // Email is optional in most cases
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Time validation utilities
export const validateTimeFormat = (time: string): boolean => {
  if (!time) return false;
  const [hours, minutes] = time.split(':').map(Number);
  return (
    !isNaN(hours) && !isNaN(minutes) &&
    hours >= 0 && hours <= 23 &&
    minutes >= 0 && minutes <= 59
  );
};

export const validateTimeRange = (startTime: string, endTime: string): { isValid: boolean; error?: string } => {
  if (!validateTimeFormat(startTime) || !validateTimeFormat(endTime)) {
    return { isValid: false, error: 'Invalid time format' };
  }

  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  // Calculate total minutes accounting for overnight shifts
  const totalMinutes = endMinutes >= startMinutes
    ? endMinutes - startMinutes  // Same day
    : (24 * 60 - startMinutes) + endMinutes;  // Overnight shift

  // Validate minimum duration (30 minutes)
  if (totalMinutes < 30) {
    return { isValid: false, error: 'Minimum overtime duration is 30 minutes' };
  }
  
  // Validate maximum duration (24 hours)
  if (totalMinutes > 24 * 60) {
    return { isValid: false, error: 'Maximum overtime duration is 24 hours' };
  }

  return { isValid: true };
};

// Required field validation
export const validateRequired = (value: string | null | undefined, fieldName: string): { isValid: boolean; error?: string } => {
  const trimmedValue = typeof value === 'string' ? value.trim() : '';
  if (!trimmedValue) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true };
};

// Employee form validation
export interface EmployeeFormData {
  name: string;
  email: string;
  department: string;
  position?: string;
  employee_id?: string;
  needsShiftAssignment?: boolean;
  selectedShift?: string;
}

export const validateEmployeeForm = (data: EmployeeFormData): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  // Required fields
  const nameValidation = validateRequired(data.name, 'Full Name');
  if (!nameValidation.isValid) {
    errors.name = nameValidation.error!;
  }

  const deptValidation = validateRequired(data.department, 'Department');
  if (!deptValidation.isValid) {
    errors.department = deptValidation.error!;
  }

  // Optional email validation
  if (data.email && !validateEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Shift assignment validation
  if (data.needsShiftAssignment && !data.selectedShift) {
    errors.selectedShift = 'Please select a shift';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Overtime form validation
export interface OvertimeFormData {
  selectedEmployee?: Record<string, unknown>;
  fromDate: string;
  startTime: string;
  endTime: string;
  justification: string;
  needsShiftSelection?: boolean;
  selectedShiftData?: Record<string, unknown>;
}

export const validateOvertimeForm = (data: OvertimeFormData): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  // Required field validations
  if (!data.selectedEmployee) {
    errors.selectedEmployee = 'Please select an employee';
  }

  const dateValidation = validateRequired(data.fromDate, 'Overtime Date');
  if (!dateValidation.isValid) {
    errors.fromDate = dateValidation.error!;
  }

  const startTimeValidation = validateRequired(data.startTime, 'Start Time');
  if (!startTimeValidation.isValid) {
    errors.startTime = startTimeValidation.error!;
  }

  const endTimeValidation = validateRequired(data.endTime, 'End Time');
  if (!endTimeValidation.isValid) {
    errors.endTime = endTimeValidation.error!;
  }

  const justificationValidation = validateRequired(data.justification, 'Justification');
  if (!justificationValidation.isValid) {
    errors.justification = justificationValidation.error!;
  }

  // Time range validation
  if (data.startTime && data.endTime) {
    const timeRangeValidation = validateTimeRange(data.startTime, data.endTime);
    if (!timeRangeValidation.isValid) {
      errors.timeRange = timeRangeValidation.error!;
    }
  }

  // Shift selection validation
  if (data.needsShiftSelection && data.selectedShiftData === undefined) {
    errors.shiftData = 'Please wait for shift information to load';
  }

  if (data.needsShiftSelection && data.selectedShiftData === null) {
    errors.shiftSelection = 'Please select a shift from the dropdowns above';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Name splitting utility for full name -> first/last name
export const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);
  
  if (nameParts.length === 0) {
    return { firstName: '', lastName: '' };
  }
  
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  }
  
  // First part is firstName, rest combined as lastName
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(' ');
  
  return { firstName, lastName };
};

// Common validation patterns
export const VALIDATION_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phoneNumber: /^[+]?[1-9][\d]{0,15}$/,
  employeeId: /^[A-Za-z0-9-_]+$/,
  time: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
} as const;
